import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import crossIcon from "../../../Assets/images/cross_icon.png";
import styles from "./ConfirmationPop.module.scss";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useCreateNoteMutation, useUpdateNoteMutation } from "store/services/V2/notes";
import { userFullName, successMessageToast } from "helpers/common";
import { useParams } from "react-router-dom";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { User } from "react-feather";
import LoadingButton from "components/shared/LoadingButton";
import { useLazyTherapistLookupQuery, useSubAdminLookupQuery, useSuperAdminLookupQuery, useTherapistLookupQuery } from "store/services/utility";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import { useUpdateWorkshopMutation } from "store/services/workshop";

const validationSchema = Yup.object({
  selectedCollaborators: Yup.array()
    .min(1, "Please select at least one collaborator.")
    .required("Please select at least one collaborator."),
});

const AddWorkshopCollaboratorsModal = ({
  title,
  onCancel,
  confirmBtnText = "Save",
  show,
  setShow,
  therapists,
  noteTableId,
  noteOwner,
}) => {

  const formik = useFormikContext() ?? {}
  const { id: workshopId } = useParams();
  const [initialValues, setInitialValues] = useState({ selectedCollaborators: [] })
  const profile = useSelector((state) => state?.auth?.user)

  const [updateWorkshop, { isLoading: updateWorkshopLoading }] = useUpdateWorkshopMutation();
  const { data: therapistsListData, isFetching: therapistsListLoading } = useTherapistLookupQuery({ skip_test: true, notes_collab: true });
  const { data: subAdmins, isFetching: subAdminsFetching } = useSubAdminLookupQuery({ skip_test: true, notes_collab: true });
  const { data: superAdmins, isFetching: superAdminsFetching } = useSuperAdminLookupQuery({ skip_test: true });

  const therapistsList = useMemo(() => {
    if (!therapistsListData?.length) return [];

    return therapistsListData
      .filter(item => item?.id !== profile?.id && item?.id !== noteOwner?.id)
      .map(item => ({ ...item, type: "Therapist" }));
  }, [therapistsListData, profile?.id]);

  const subAdminsList = useMemo(() => {
    if (!subAdmins?.length) return [];

    return subAdmins
      .filter(item => item?.id !== profile?.id && item?.id !== noteOwner?.id)
      .map(item => ({ ...item, type: "Sub-Admin" }));
  }, [subAdmins, profile?.id]);

  const superAdminsList = useMemo(() => {
    if (!superAdmins?.length) return [];

    return superAdmins
      .filter(item => item?.id !== profile?.id)
      .map(item => ({ ...item, type: "Admin" }));
  }, [superAdmins, profile?.id]);


  const [usersList, setUsersList] = useState([])
  const [searchText, setSearchText] = useState("");

  const filteredUsers = usersList?.length > 0 ? usersList?.filter((item) => {
    const fullName = userFullName(item)?.toLowerCase() || "";
    const email = item?.email?.toLowerCase() || "";
    const firstName = item?.first_name?.toLowerCase() || "";
    const lastName = item?.last_name?.toLowerCase() || "";

    return (
      fullName.includes(searchText) ||
      email.includes(searchText) ||
      firstName.includes(searchText) ||
      lastName.includes(searchText)
    );
  }) : [];

  const handleClose = () => {
    setShow(false);
    setSearchText("")
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const data = {
      title: formik?.values?.title,
      link: formik?.values?.link,
      description: formik?.values?.description,
      date: formik?.values?.date,
      collaboratorData: values?.selectedCollaborators
    };
    if (workshopId || noteTableId) {
      try {
        await updateWorkshop({ data, workshopId: workshopId || noteTableId }).unwrap()
        handleClose();
        resetForm();
      } catch (err) {
        console.error("Error submitting:", err);
      } finally {
        setSubmitting(false);
      }
    }
    else {
      const filteredTherapists =
        usersList?.filter((item) =>
          values?.selectedCollaborators?.includes(item?.id)
        );
      formik.setFieldValue('therapistData', filteredTherapists)
      handleClose()
    }
  };

  const allIds = filteredUsers?.map((u) => u.id) || [];

  useEffect(() => {
    if (therapists && subAdmins && superAdmins) {
      const list = [...therapistsList, ...subAdminsList, ...superAdminsList];
      setUsersList(list);
    }
  }, [therapists, therapistsList, subAdminsList, superAdminsList]);

  useEffect(() => {
    if (therapists?.length) {
      setInitialValues((prev) => ({ ...prev, selectedCollaborators: therapists?.length ? therapists?.map((item) => item?.user_id || item?.id) : [] }))
    }
    else {
      setInitialValues((prev) => ({ ...prev, selectedCollaborators: [] }))
    }
  }, [therapists])

  return (
    <Modal
      show={show}
      className={`${styles.ConfirmationPop} transparentModal modal-v2`}
      onHide={onCancel}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Body className={`${styles.modalBody}`}>
        {(therapistsListLoading) && <LoadingOverlay />}
        <div className="text-center mx-auto">
          {/* Header */}
          <div
            className="section-title d-flex align-items-center justify-content-between"
            style={{ borderBottom: "1px solid #93278F" }}
          >
            <h3 className="mb-0 pb-0 section-title border-bottom-0" style={{ fontSize: "14px" }}>
              ADD COLLABORATORS
            </h3>
            <img
              onClick={handleClose}
              width={16}
              height={16}
              src={crossIcon}
              alt="crossIcon"
              style={{ cursor: "pointer" }}
            />
          </div>

          {/* Formik Form */}
          <Formik
            initialValues={initialValues}
            // validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, touched, isSubmitting }) => (
              <Form>
                <Row>
                  <Col lg="8">
                    <div className="d-flex w-100 align-items-center gap-3">
                      <input
                        onChange={(e) => handleSearch(e.target.value)}
                        className={` readonly-box h-auto search-input-filter-v2`}
                        placeholder="Search by name, email"
                      />
                    </div>
                    <div className="text-start mt-3">
                      <label htmlFor="" className="discharge-screen-label mt-0">
                        SELECT COLLABORATOR
                      </label>
                    </div>
                  </Col>
                </Row>

                {/* Table with Scrollbar */}
                <div
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#b46c9f #f0f0f0",
                    // border: "1px solid #ccc",
                    // borderRadius: "8px",
                  }}
                >
                  <table className="w-100">
                    <thead>
                      <tr>
                        <th
                          style={{
                            padding: "12px 10px",
                            borderBottom: "2px solid #8F8F8F",
                            textAlign: "left",
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <input
                              type="checkbox"
                              style={{
                                accentColor: "#b46c9f",
                                height: 20,
                                width: 20,
                              }}
                              checked={allIds.every((id) => values.selectedCollaborators.includes(id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFieldValue("selectedCollaborators", allIds);
                                } else {
                                  setFieldValue("selectedCollaborators", []);
                                }
                              }}
                            />
                            <span>Select All</span>
                          </div>
                        </th>

                        <th
                          style={{
                            padding: "12px 10px",
                            borderBottom: "2px solid #8F8F8F",
                            textAlign: "center",
                          }}
                        >
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers?.map((item, index) => {
                        const isChecked = values.selectedCollaborators.some(
                          (sel) => sel === item?.id
                        );
                        return (
                          <tr key={item?.id}>
                            <td
                              style={{
                                padding: "12px 10px",
                                borderBottom: "1px solid #8F8F8F",
                                borderRadius: "0px"
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  style={{
                                    accentColor: "#b46c9f",
                                    height: 20,
                                    width: 20,
                                    marginTop: "-3px",
                                  }}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue("selectedCollaborators", [
                                        ...values.selectedCollaborators,
                                        item?.id,
                                      ]);
                                    } else {
                                      setFieldValue(
                                        "selectedCollaborators",
                                        values.selectedCollaborators.filter(
                                          (sel) => sel !== item?.id
                                        )
                                      );
                                    }
                                  }}
                                />
                                {
                                  item?.therapist_detail?.profile_picture ?
                                    <img
                                      // src={noteToShow?.noteOwner?.therapist_detail?.profile_picture === null ? p1 : (noteToShow?.noteOwner?.therapist_detail?.profile_picture == "undefined" ? imageLoader : noteToShow?.noteOwner?.therapist_detail?.profile_picture ? noteToShow?.noteOwner?.therapist_detail?.profile_picture : p1)}
                                      src={item?.therapist_detail?.profile_picture}
                                      // src={noteToShow?.noteOwner?.therapist_detail?.profile_picture || p1}
                                      alt=""
                                      className="img-fluid object-fit-cover rounded-circle border border-white"
                                      style={{ height: 30, width: 30 }}
                                    /> :
                                    <User size={20} />
                                }
                                <span>{`${userFullName(item)}`}</span>
                              </div>
                            </td>
                            <td
                              className="text-center"
                              style={{
                                padding: "12px 10px",
                                borderBottom: "1px solid #8F8F8F",
                                borderRadius: "0px",
                                width: "100px"
                              }}
                            >
                              {item?.type}
                              {/* {item?.discipline?.map((disc, i) => (
                                <span
                                  key={i}
                                  style={{
                                    padding: "6px 10px",
                                    marginRight: "4px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f3e6f0",
                                    color: "#93278F",
                                    fontWeight: 500,
                                    display: "inline-block",
                                  }}
                                >
                                  {disc}
                                </span>
                              ))} */}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {touched.selectedCollaborators && errors.selectedCollaborators && (
                  <div className="text-danger mt-2" style={{ fontSize: "13px" }}>
                    {errors.selectedCollaborators}
                  </div>
                )}

                {/* Buttons */}
                <div className="d-flex justify-content-end mt-4 gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: "#ccc",
                      // color: "#000",
                      border: "none",
                      borderRadius: "0",
                      padding: "6px 16px",
                    }}
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    type="submit"
                    isLoading={isSubmitting}
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#93278F",
                      border: "none",
                      borderRadius: "0",
                      padding: "6px 16px",
                    }}
                  >
                    {confirmBtnText}
                  </LoadingButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddWorkshopCollaboratorsModal;
